import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { UseValueSetExpandResult } from '../../hooks/useValueSetExpand';
import { DatePicker } from '../atoms/DatePicker';
import { Button } from '../ui/button';
import { Field, FieldGroup, FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { ValueSetCombobox, type ValueSetCode } from './ValueSetCombobox';

export type { ValueSetCode };

const formSchema = z.object({
  code: z.custom<ValueSetCode | undefined>().refine((v): v is ValueSetCode => v !== undefined),
  clinicalStatus: z.custom<ValueSetCode | undefined>().refine((v): v is ValueSetCode => v !== undefined),
  onsetDate: z.string(),
  notes: z.string().trim(),
});

export type AddConditionFormValues = z.infer<typeof formSchema>;

export interface AddConditionFormProps {
  onCreate: (values: AddConditionFormValues) => Promise<void>;
  onClose: () => void;
  isCreating: boolean;
  code: UseValueSetExpandResult;
  clinicalStatus: UseValueSetExpandResult;
}

export function AddConditionForm({
  onCreate,
  onClose,
  isCreating,
  code,
  clinicalStatus,
}: AddConditionFormProps): React.ReactNode {
  const { t } = useTranslation('records');

  const form = useForm({
    defaultValues: {
      code: undefined as ValueSetCode | undefined,
      clinicalStatus: undefined as ValueSetCode | undefined,
      onsetDate: '',
      notes: '',
    },
    validators: { onChange: formSchema, onMount: formSchema, onSubmit: formSchema },
    onSubmit: async ({ value }) => {
      await onCreate(formSchema.parse(value));
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-3"
    >
      <FieldGroup>
        <form.Field name="code">
          {(field) => (
            <ValueSetCombobox
              id={field.name}
              label={t('conditionCode')}
              placeholder={t('conditionCodePlaceholder')}
              items={code.items}
              value={field.state.value ?? null}
              onValueChange={(value) => field.handleChange(value ?? undefined)}
              onQueryChange={code.onQueryChange}
              isLoading={code.isFetching}
              error={code.error}
              emptyMessage={t('noMatchingResults')}
            />
          )}
        </form.Field>

        <form.Field name="clinicalStatus">
          {(field) => (
            <ValueSetCombobox
              id={field.name}
              label={t('clinicalStatus')}
              placeholder={t('clinicalStatusPlaceholder')}
              items={clinicalStatus.items}
              value={field.state.value ?? null}
              onValueChange={(value) => field.handleChange(value ?? undefined)}
              onQueryChange={clinicalStatus.onQueryChange}
              isLoading={clinicalStatus.isFetching}
              error={clinicalStatus.error}
              emptyMessage={t('noMatchingResults')}
            />
          )}
        </form.Field>

        <form.Field name="onsetDate">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('onsetDate')}</FieldLabel>
              <DatePicker
                id={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(value) => field.handleChange(value)}
                aria-label={t('onsetDate')}
              />
            </Field>
          )}
        </form.Field>

        <form.Field name="notes">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('notes')}</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('notePlaceholder')}
                aria-label={t('notes')}
              />
            </Field>
          )}
        </form.Field>
      </FieldGroup>

      <div className="flex gap-2">
        <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit || isSubmitting || isCreating}
              className="flex-1"
              aria-label={t('saveNewCondition')}
            >
              {isSubmitting || isCreating ? <Spinner className="size-4" /> : t('add')}
            </Button>
          )}
        </form.Subscribe>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClose}
          disabled={isCreating}
          className="flex-1"
          aria-label={t('cancelAddCondition')}
        >
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
}
